import {
  FirstStockLocationNotice,
  NoStockLocationNotice,
} from '../../app/no-stock-location-notice.js';
import { CollectionTable, CollectionCards } from '../../app/collection-view.js';
import { BarcodeReaderField } from '../../app/barcode-reader-field.js';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Combobox,
  DatePicker,
  DateRangePicker,
  FormErrorSummary,
  IconButton,
  Input,
  Modal,
  Select,
  StatusPill,
  Textarea,
  formatCivilRange,
  todayCivilDate,
  useSnackbar,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, ClipboardCheck, History, Plus, Undo2 } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import {
  getApiError,
  inventoryApi,
  inventoryMovementsApi,
  type InventoryLossReason,
  type InventoryMovement,
  type InventoryMovementKind,
  type InventoryMovementSort,
  type InventoryProduct,
  type InventoryQuantityInput,
} from '../../lib/api.js';
import { factTimeZone, formatDate } from '../../lib/time-zone.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  FilterGroup,
  LoadingCard,
  Pagination,
  RequestAlert,
  SortBar,
  SortableColumn,
} from '../../app/data-view.js';
import type { InventoryView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import {
  BASE_QUANTITY,
  lossReasonLabels,
  lossReasonOptions,
  movementKindLabels,
  shortUnit,
  trimQuantity,
  type InventoryCapabilities,
} from './shared.js';
import { locationLabel, useInventoryUnits } from './inventory-units.js';

const kindOptions = [
  { value: 'ALL' as const, label: 'Todos' },
  ...(Object.keys(movementKindLabels) as InventoryMovementKind[]).map((value) => ({
    value,
    label: movementKindLabels[value],
  })),
];
const flowOptions = [
  { value: 'ALL' as const, label: 'Tudo' },
  { value: 'IN' as const, label: 'Entradas' },
  { value: 'OUT' as const, label: 'Saídas' },
];
const columns: Array<{ sort: InventoryMovementSort; label: string }> = [
  { sort: 'occurredAt', label: 'Quando' },
  { sort: 'quantity', label: 'Quantidade' },
];
const quantity = z.string().trim().regex(BASE_QUANTITY, 'Informe um número com até 6 casas.');

const movementSchema = z.object({
  kind: z.enum(['ENTRY', 'CONSUMPTION', 'LOSS']),
  productId: z.string().uuid('Escolha o produto.'),
  // Vazio só vale na primeira entrada da unidade: quem confere é `save`, que conhece os locais.
  locationId: z.string(),
  quantity,
  packageId: z.string(),
  lotId: z.string(),
  lotCode: z.string().trim().max(120),
  lotExpiresOn: z.string(),
  lossReason: z.string(),
  note: z.string().trim().max(240),
  occurredOn: z.string(),
});
type MovementForm = z.infer<typeof movementSchema>;

const adjustmentSchema = z.object({
  productId: z.string().uuid('Escolha o produto.'),
  locationId: z.string().uuid('Escolha o local.'),
  lotId: z.string(),
  countedQuantity: quantity,
  note: z.string().trim().min(3, 'Explique a diferença encontrada.').max(240),
  occurredOn: z.string(),
});
type AdjustmentForm = z.infer<typeof adjustmentSchema>;

const transferSchema = z
  .object({
    productId: z.string().uuid('Escolha o produto.'),
    originLocationId: z.string().uuid('Escolha a origem.'),
    destinationLocationId: z.string().uuid('Escolha o destino.'),
    quantity,
    lotId: z.string(),
    note: z.string().trim().max(240),
    occurredOn: z.string(),
  })
  .refine((value) => value.originLocationId !== value.destinationLocationId, {
    path: ['destinationLocationId'],
    message: 'Escolha um destino diferente da origem.',
  });
type TransferForm = z.infer<typeof transferSchema>;

type Dialog = 'movement' | 'adjustment' | 'transfer';

export function MovementsPanel({
  capabilities,
  timeZone,
  view,
  onView,
}: {
  capabilities: InventoryCapabilities;
  timeZone: string;
  view: InventoryView;
  onView: ViewSetter<InventoryView>;
}) {
  const { showUnits, consolidated } = useInventoryUnits();
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [dialog, setDialog] = useState<Dialog>();
  const [reversalTarget, setReversalTarget] = useState<InventoryMovement>();
  const [reversalError, setReversalError] = useState<string>();
  const locations = useQuery({
    queryKey: ['company', 'inventory', 'locations'],
    queryFn: () => inventoryApi.listLocations('ALL'),
    retry: false,
  });
  const filters = {
    ...(view.search.trim() ? { search: view.search.trim() } : {}),
    ...(view.location ? { locationId: view.location } : {}),
    ...(view.from ? { from: view.from } : {}),
    ...(view.to ? { to: view.to } : {}),
    kind: view.movementKind,
    flow: view.flow,
    sort: view.movementSort,
    sortDirection: view.direction,
    limit: view.limit,
    offset: view.offset,
  };
  const page = useQuery({
    queryKey: ['company', 'inventory', 'movements', filters],
    queryFn: () => inventoryMovementsApi.list(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });

  function toggleSort(column: InventoryMovementSort) {
    if (view.movementSort === column)
      onView({ direction: view.direction === 'asc' ? 'desc' : 'asc' });
    else onView({ movementSort: column, direction: 'desc' });
  }

  async function reverse(movement: InventoryMovement) {
    setReversalError(undefined);
    try {
      await inventoryMovementsApi.reverse(movement.id);
      await queryClient.invalidateQueries({ queryKey: ['company', 'inventory'] });
      setReversalTarget(undefined);
      snackbar.success('Movimento estornado.');
    } catch (error) {
      setReversalError(getApiError(error));
    }
  }

  async function saved(message: string) {
    await queryClient.invalidateQueries({ queryKey: ['company', 'inventory'] });
    setDialog(undefined);
    snackbar.success(message);
  }

  const total = page.data?.total ?? 0;
  const locationName = locations.data?.find((item) => item.id === view.location)?.displayName;
  const chips = [
    ...(view.movementKind === 'ALL'
      ? []
      : [
          {
            key: 'kind',
            label: movementKindLabels[view.movementKind],
            onRemove: () => onView({ movementKind: 'ALL' }),
          },
        ]),
    ...(view.flow === 'ALL'
      ? []
      : [
          {
            key: 'flow',
            label: flowOptions.find((item) => item.value === view.flow)!.label,
            onRemove: () => onView({ flow: 'ALL' }),
          },
        ]),
    ...(locationName
      ? [{ key: 'location', label: locationName, onRemove: () => onView({ location: '' }) }]
      : []),
    ...((view.from ?? view.to)
      ? [
          {
            key: 'range',
            label: formatCivilRange({ from: view.from, to: view.to }, 'Qualquer dia'),
            onRemove: () => onView({ from: null, to: null }),
          },
        ]
      : []),
  ];

  return (
    <>
      {capabilities.canRegisterMovements ? (
        <div className="mb-5 flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={() => setDialog('adjustment')}>
            <ClipboardCheck className="size-4" />
            Ajustar contagem
          </Button>
          <Button variant="outline" onClick={() => setDialog('transfer')}>
            <ArrowLeftRight className="size-4" />
            Transferir
          </Button>
          <Button onClick={() => setDialog('movement')}>
            <Plus className="size-4" />
            Registrar movimento
          </Button>
        </div>
      ) : null}

      <FilterBar
        searchId="inventory-movement-search"
        searchLabel="Buscar movimento"
        searchPlaceholder="Produto, local, lote ou observação"
        search={view.search}
        onSearchChange={(search) => onView({ search }, { replace: true })}
        chips={chips}
        onClear={() =>
          onView({ movementKind: 'ALL', flow: 'ALL', location: '', from: null, to: null })
        }
        count={`${total} ${total === 1 ? 'movimento' : 'movimentos'}`}
        resultsLabel={`Ver ${total} ${total === 1 ? 'movimento' : 'movimentos'}`}
        pageSize={view.limit}
        onPageSizeChange={(limit) => onView({ limit })}
      >
        <FilterGroup
          label="Tipo"
          description="Filtrar movimentos por tipo"
          options={kindOptions}
          value={view.movementKind}
          onChange={(movementKind) => onView({ movementKind })}
        />
        <FilterGroup
          label="Direção"
          description="Filtrar movimentos por entrada ou saída"
          options={flowOptions}
          value={view.flow}
          onChange={(flow) => onView({ flow })}
        />
        <FilterGroup
          label="Local"
          description="Filtrar movimentos por local"
          options={[
            { value: '', label: 'Todos' },
            ...(locations.data ?? []).map((item) => ({
              value: item.id,
              label: showUnits
                ? `${item.displayName} · ${item.businessUnitName}`
                : item.displayName,
            })),
          ]}
          value={view.location}
          onChange={(location) => onView({ location })}
        />
        <div className="min-w-0">
          <span className="mb-1.5 block text-xs font-semibold text-content/70">Quando</span>
          <DateRangePicker
            label="Dias do movimento"
            value={{ from: view.from, to: view.to }}
            onValueChange={(next) => onView({ from: next.from, to: next.to })}
            today={todayCivilDate(timeZone)}
          />
        </div>
      </FilterBar>

      {page.isLoading ? (
        <LoadingCard label="Carregando movimentos" />
      ) : page.error ? (
        <ErrorCard message={getApiError(page.error)} />
      ) : total === 0 ? (
        <EmptyCard
          icon={History}
          title="Nenhum movimento encontrado"
          description="Ajuste os filtros ou registre a primeira entrada de mercadoria."
        />
      ) : (
        <div className="hue-responsive-collection overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          <CollectionCards>
            <div className="hue-collection-cards">
              <SortBar
                label="Ordenação dos movimentos"
                columns={columns}
                sort={view.movementSort}
                direction={view.direction}
                onToggle={toggleSort}
              />
              <ul className="divide-y divide-surface-border">
                {page.data?.movements.map((movement) => (
                  <li key={movement.id} className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <strong className="block truncate text-sm">
                          {movement.product.displayName}
                        </strong>
                        <span className="mt-1 block text-xs text-content-muted">
                          {movementKindLabels[movement.kind]} ·{' '}
                          {locationLabel(movement.location, showUnits)}
                        </span>
                        <span className="mt-1 block text-sm font-semibold tabular-nums">
                          {signOf(movement)}
                          {trimQuantity(movement.quantity)} {shortUnit(movement.product.baseUnit)}
                        </span>
                        {movement.reversedByMovementId ? (
                          <StatusPill tone="neutral">Estornado</StatusPill>
                        ) : null}
                      </div>
                      <Reversal
                        movement={movement}
                        capabilities={capabilities}
                        onReverse={() => setReversalTarget(movement)}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </CollectionCards>
          <CollectionTable>
            <table className="w-full border-collapse text-left">
              <caption className="sr-only">Movimentos de estoque da empresa</caption>
              <thead>
                <tr className="border-b border-surface-border">
                  <th scope="col" className="px-5 py-3 text-xs font-semibold text-content/70">
                    Movimento
                  </th>
                  {columns.map((column) => (
                    <SortableColumn
                      key={column.sort}
                      column={column.sort}
                      label={column.label}
                      sort={view.movementSort}
                      direction={view.direction}
                      onToggle={toggleSort}
                    />
                  ))}
                  <th scope="col" className="px-5 py-3 text-xs font-semibold text-content/70">
                    Detalhe
                  </th>
                  <th className="w-16">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {page.data?.movements.map((movement) => (
                  <tr key={movement.id}>
                    <td className="px-5 py-4">
                      <strong className="block truncate text-sm">
                        {movement.product.displayName}
                      </strong>
                      <span className="mt-1 block truncate text-xs text-content-muted">
                        {movementKindLabels[movement.kind]} ·{' '}
                        {locationLabel(movement.location, showUnits)}
                        {movement.lot ? ` · lote ${movement.lot.code}` : ''}
                      </span>
                      {movement.reversedByMovementId ? (
                        <StatusPill tone="neutral">Estornado</StatusPill>
                      ) : null}
                      {movement.reversesMovementId ? (
                        <StatusPill tone="neutral">Estorno</StatusPill>
                      ) : null}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {formatDate(
                        movement.occurredAt,
                        factTimeZone(movement, timeZone, consolidated),
                      )}
                    </td>
                    <td className="px-5 py-4 text-sm font-semibold tabular-nums">
                      {signOf(movement)}
                      {trimQuantity(movement.quantity)} {shortUnit(movement.product.baseUnit)}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">{detailOf(movement)}</td>
                    <td className="px-3 py-4">
                      <Reversal
                        movement={movement}
                        capabilities={capabilities}
                        onReverse={() => setReversalTarget(movement)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CollectionTable>
          <Pagination
            total={total}
            limit={view.limit}
            offset={view.offset}
            onOffsetChange={(offset) => onView({ offset })}
            itemLabel={(count) => (count === 1 ? 'movimento' : 'movimentos')}
          />
        </div>
      )}

      {dialog === 'movement' ? (
        <MovementDialog
          timeZone={timeZone}
          onClose={() => setDialog(undefined)}
          onSaved={() => saved('Movimento registrado.')}
        />
      ) : null}
      {dialog === 'adjustment' ? (
        <AdjustmentDialog
          timeZone={timeZone}
          onClose={() => setDialog(undefined)}
          onSaved={(adjusted) => {
            if (adjusted) {
              void saved('Contagem ajustada.');
              return;
            }
            /// Contagem que bate com o saldo não gera fato nenhum — e dizer isso é melhor do que
            /// fechar em silêncio, que pareceria que o ajuste falhou.
            setDialog(undefined);
            snackbar.success('A contagem já batia com o saldo: nada a ajustar.');
          }}
        />
      ) : null}
      {dialog === 'transfer' ? (
        <TransferDialog
          timeZone={timeZone}
          onClose={() => setDialog(undefined)}
          onSaved={() => saved('Transferência registrada.')}
        />
      ) : null}

      {reversalTarget ? (
        <Modal
          open
          onOpenChange={() => setReversalTarget(undefined)}
          title="Estornar movimento"
          description="O fato original continua no histórico. O estorno é um movimento novo, na direção oposta, apontando para ele."
        >
          <p className="text-sm">
            Estornar {movementKindLabels[reversalTarget.kind].toLocaleLowerCase('pt-BR')} de{' '}
            <strong>
              {trimQuantity(reversalTarget.quantity)} {shortUnit(reversalTarget.product.baseUnit)}
            </strong>{' '}
            de {reversalTarget.product.displayName}?
          </p>
          {reversalError ? <RequestAlert message={reversalError} /> : null}
          <div className="mt-7 flex justify-end gap-3 border-t border-surface-border pt-5">
            <Button variant="outline" onClick={() => setReversalTarget(undefined)}>
              Cancelar
            </Button>
            <Button onClick={() => void reverse(reversalTarget)}>Estornar movimento</Button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

function signOf(movement: InventoryMovement): string {
  return movement.direction === 'IN' ? '+' : '−';
}

function detailOf(movement: InventoryMovement): string {
  if (movement.lossReason) return lossReasonLabels[movement.lossReason];
  if (movement.countedQuantity) return `Contado: ${trimQuantity(movement.countedQuantity)}`;
  if (movement.package)
    return `${trimQuantity(movement.packageQuantity!)} × ${movement.package.displayName}`;
  return movement.note ?? '—';
}

/// Estorno de estorno não existe: o caminho de volta é lançar o fato de novo, não empilhar espelhos.
function Reversal({
  movement,
  capabilities,
  onReverse,
}: {
  movement: InventoryMovement;
  capabilities: InventoryCapabilities;
  onReverse: () => void;
}) {
  if (!capabilities.canReverseMovements) return null;
  if (movement.reversedByMovementId || movement.reversesMovementId) return null;
  return (
    <IconButton
      label="Estornar movimento"
      icon={<Undo2 className="size-4" />}
      onClick={onReverse}
    />
  );
}

/// Catálogo de produtos e locais para os diálogos. A lista é curta por natureza — e quando deixar
/// de ser, vira busca no servidor, não um `select` de mil linhas.
function useCatalog() {
  const products = useQuery({
    queryKey: ['company', 'inventory', 'catalog', 'products'],
    queryFn: () =>
      inventoryApi.listProducts({
        activity: 'ACTIVE',
        lotTracking: 'ALL',
        sort: 'displayName',
        direction: 'asc',
        limit: 100,
        offset: 0,
      }),
    retry: false,
  });
  const locations = useQuery({
    queryKey: ['company', 'inventory', 'locations'],
    queryFn: () => inventoryApi.listLocations('ACTIVE'),
    retry: false,
  });
  return {
    products: products.data?.products ?? [],
    locations: locations.data ?? [],
    loading: products.isLoading || locations.isLoading,
  };
}

/// O catálogo de produtos cresce com a empresa: escolher é digitar e achar, nunca rolar uma lista.
function ProductField({
  products,
  value,
  error,
  onChange,
}: {
  products: InventoryProduct[];
  value: string;
  error?: string | undefined;
  onChange: (productId: string) => void;
}) {
  return (
    <Combobox
      label="Produto"
      placeholder="Buscar produto"
      selectedLabel={products.find((item) => item.id === value)?.displayName ?? ''}
      options={products.map((item) => ({ value: item.id, label: item.displayName }))}
      emptyLabel="Nenhum produto com esse nome"
      error={error}
      onClear={() => onChange('')}
      onSelect={(choice) => {
        if (choice.kind === 'option') onChange(choice.value);
      }}
    />
  );
}

/// Data do formulário no seletor do sistema; o formulário guarda texto, e vazio é "sem data".
function DateField({
  label,
  value,
  timeZone,
  error,
  onChange,
}: {
  label: string;
  value: string;
  timeZone: string;
  error?: string | undefined;
  onChange: (value: string) => void;
}) {
  return (
    <DatePicker
      label={label}
      value={value || null}
      today={todayCivilDate(timeZone)}
      error={error}
      onValueChange={(date) => onChange(date ?? '')}
    />
  );
}

/// Com um único local não há o que perguntar: o campo já vem preenchido.
function useSingleLocation(locations: Array<{ id: string }>, choose: (locationId: string) => void) {
  const only = locations.length === 1 ? locations[0]!.id : undefined;
  useEffect(() => {
    if (only) choose(only);
    // `choose` é refeito a cada render; o que importa é qual é o único local.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [only]);
}

function locationOptions(locations: Array<{ id: string; displayName: string }>, label: string) {
  return [
    { value: '', label },
    ...locations.map((item) => ({ value: item.id, label: item.displayName })),
  ];
}

function MovementDialog({
  timeZone,
  onClose,
  onSaved,
}: {
  timeZone: string;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const formId = useId();
  const { products: catalogProducts, locations, loading } = useCatalog();
  const [scannedProduct, setScannedProduct] = useState<InventoryProduct>();
  const products = scannedProduct
    ? [scannedProduct, ...catalogProducts.filter((item) => item.id !== scannedProduct.id)]
    : catalogProducts;
  const [requestError, setRequestError] = useState<string>();
  const form = useForm<MovementForm>({
    resolver: zodResolver(movementSchema),
    defaultValues: {
      kind: 'ENTRY',
      productId: '',
      locationId: '',
      quantity: '',
      packageId: '',
      lotId: '',
      lotCode: '',
      lotExpiresOn: '',
      lossReason: 'EXPIRATION',
      note: '',
      occurredOn: todayCivilDate(timeZone),
    },
  });
  const kind = useWatch({ control: form.control, name: 'kind' });
  const productId = useWatch({ control: form.control, name: 'productId' });
  const occurredOn = useWatch({ control: form.control, name: 'occurredOn' });
  const locationId = useWatch({ control: form.control, name: 'locationId' });
  const amount = useWatch({ control: form.control, name: 'quantity' });
  const packageId = useWatch({ control: form.control, name: 'packageId' });
  const lotId = useWatch({ control: form.control, name: 'lotId' });
  const lossReason = useWatch({ control: form.control, name: 'lossReason' });
  const lotExpiresOn = useWatch({ control: form.control, name: 'lotExpiresOn' });
  const product = products.find((item) => item.id === productId);
  const chosenPackage = product?.packages.find((item) => item.id === packageId);
  const tracked = product?.lotTracking === 'REQUIRED';
  const incoming = kind === 'ENTRY';
  const noLocation = !loading && locations.length === 0;
  useSingleLocation(locations, (id) => form.setValue('locationId', id));

  /// Só faz sentido sugerir lote na saída: na entrada o lote está chegando agora.
  const suggestions = useQuery({
    queryKey: ['company', 'inventory', 'lots', productId, locationId, amount],
    queryFn: () =>
      inventoryMovementsApi.suggestLots({
        productId,
        locationId,
        quantity: amount.replace(',', '.'),
      }),
    enabled: Boolean(tracked && !incoming && productId && locationId && BASE_QUANTITY.test(amount)),
    retry: false,
  });
  useEffect(() => {
    const suggested = suggestions.data?.find((item) => item.suggested);
    if (suggested) form.setValue('lotId', suggested.lot.id);
  }, [form, suggestions.data]);

  async function readBarcode(code: string) {
    setRequestError(undefined);
    try {
      const page = await inventoryApi.listProducts({
        activity: 'ACTIVE',
        lotTracking: 'ALL',
        sort: 'displayName',
        direction: 'asc',
        search: code,
        limit: 100,
        offset: 0,
      });
      const hits = page.products
        .filter((item) => item.active)
        .flatMap((item) =>
          item.packages
            .filter((pack) => pack.active && pack.barcode === code)
            .map((pack) => ({ item, pack })),
        );
      const hit = hits.length === 1 ? hits[0] : undefined;
      if (!hit) {
        setRequestError(
          'Código não encontrado em uma apresentação ativa. Confira ou cadastre o EAN em Catálogo / Produtos e leia novamente.',
        );
        return;
      }
      setScannedProduct(hit.item);
      if (form.getValues('productId') !== hit.item.id) {
        form.setValue('lotCode', '');
        form.setValue('lotExpiresOn', '');
      }
      form.setValue('productId', hit.item.id, { shouldValidate: true });
      form.setValue('packageId', hit.pack.id);
      form.setValue('lotId', '');
      if (!form.getValues('quantity')) form.setValue('quantity', '1');
    } catch (cause) {
      setRequestError(getApiError(cause));
    }
  }

  async function save(data: MovementForm) {
    setRequestError(undefined);
    if (!data.locationId && !(incoming && noLocation)) {
      form.setError('locationId', { message: 'Escolha o local.' });
      return;
    }
    try {
      await inventoryMovementsApi.register({
        kind: data.kind,
        productId: data.productId,
        ...(data.locationId ? { locationId: data.locationId } : {}),
        quantity: quantityOf(data.quantity, data.packageId),
        ...(tracked
          ? {
              lot: incoming
                ? { code: data.lotCode, expiresOn: data.lotExpiresOn || null }
                : { id: data.lotId },
            }
          : {}),
        ...(data.kind === 'LOSS' ? { lossReason: data.lossReason as InventoryLossReason } : {}),
        note: data.note || null,
        occurredOn: data.occurredOn,
      });
      await onSaved();
    } catch (cause) {
      setRequestError(getApiError(cause));
    }
  }

  return (
    <Modal
      open
      onOpenChange={onClose}
      title="Registrar movimento"
      description="Entrada, consumo e perda são fatos distintos, e nenhum deles é editável depois: corrigir é estornar e lançar de novo."
      footer={
        !loading ? (
          <div className="flex flex-wrap items-center justify-end gap-3">
            <FormErrorSummary form={formId} className="basis-full sm:basis-auto sm:flex-1" />
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" form={formId} loading={form.formState.isSubmitting}>
              Registrar movimento
            </Button>
          </div>
        ) : undefined
      }
    >
      {loading ? (
        <LoadingCard label="Carregando catálogo" />
      ) : (
        <form id={formId} noValidate onSubmit={(event) => void form.handleSubmit(save)(event)}>
          {incoming ? (
            <div className="mb-4">
              <BarcodeReaderField onRead={readBarcode} disabled={form.formState.isSubmitting} />
            </div>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Tipo"
              value={kind}
              options={[
                { value: 'ENTRY', label: 'Entrada de mercadoria' },
                { value: 'CONSUMPTION', label: 'Consumo' },
                { value: 'LOSS', label: 'Perda' },
              ]}
              onValueChange={(value) =>
                form.setValue('kind', value as MovementForm['kind'], { shouldValidate: true })
              }
            />
            <DateField
              label="Quando"
              timeZone={timeZone}
              value={occurredOn}
              error={form.formState.errors.occurredOn?.message}
              onChange={(value) => form.setValue('occurredOn', value, { shouldValidate: true })}
            />
            <ProductField
              products={products}
              value={productId}
              error={form.formState.errors.productId?.message}
              onChange={(value) => {
                form.setValue('productId', value, { shouldValidate: true });
                form.setValue('packageId', '');
                form.setValue('lotId', '');
              }}
            />
            {noLocation ? (
              incoming ? (
                <FirstStockLocationNotice />
              ) : (
                <NoStockLocationNotice />
              )
            ) : (
              <Select
                error={form.formState.errors.locationId?.message}
                label="Local"
                value={locationId}
                options={locationOptions(locations, 'Escolha o local')}
                onValueChange={(value) =>
                  form.setValue('locationId', value, { shouldValidate: true })
                }
              />
            )}
            {product && product.packages.filter((item) => item.active).length > 0 ? (
              <Select
                label="Contar em"
                value={packageId}
                options={[
                  { value: '', label: `Unidade-base (${shortUnit(product.baseUnit)})` },
                  ...product.packages
                    .filter((item) => item.active)
                    .map((item) => ({
                      value: item.id,
                      label: `${item.displayName} (${trimQuantity(item.baseQuantity)} ${shortUnit(product.baseUnit)})`,
                    })),
                ]}
                onValueChange={(value) => form.setValue('packageId', value)}
              />
            ) : null}
            <Input
              label={`Quantidade (${countingUnit(product, chosenPackage)})`}
              inputMode="decimal"
              error={form.formState.errors.quantity?.message}
              hint={countingHint(product, chosenPackage, amount)}
              {...form.register('quantity')}
            />
            {tracked && incoming ? (
              <>
                <Input
                  label="Lote do fabricante"
                  error={form.formState.errors.lotCode?.message}
                  hint="Código repetido reaproveita o lote já cadastrado."
                  {...form.register('lotCode')}
                />
                <DateField
                  label="Validade (opcional)"
                  timeZone={timeZone}
                  value={lotExpiresOn}
                  onChange={(value) => form.setValue('lotExpiresOn', value)}
                />
              </>
            ) : null}
            {tracked && !incoming ? (
              <div className="sm:col-span-2">
                <Select
                  label="Lote"
                  value={lotId}
                  options={[
                    { value: '', label: 'Escolha o lote' },
                    ...(suggestions.data ?? []).map((item) => ({
                      value: item.lot.id,
                      label: `${item.lot.code} · saldo ${trimQuantity(item.balance)}${
                        item.suggested ? ' · sugerido: vence primeiro' : ''
                      }`,
                    })),
                  ]}
                  onValueChange={(value) => form.setValue('lotId', value)}
                />
                <p className="mt-1 text-xs text-content-muted">
                  O sugerido é o que vence primeiro entre os que têm saldo. Você pode escolher
                  outro.
                </p>
              </div>
            ) : null}
            {kind === 'LOSS' ? (
              <div className="sm:col-span-2">
                <Select
                  label="Motivo da perda"
                  value={lossReason}
                  options={lossReasonOptions}
                  onValueChange={(value) => form.setValue('lossReason', value)}
                />
              </div>
            ) : null}
            <div className="sm:col-span-2">
              <Textarea label="Observação" rows={2} {...form.register('note')} />
            </div>
          </div>
          {requestError ? <RequestAlert message={requestError} /> : null}
        </form>
      )}
    </Modal>
  );
}

function AdjustmentDialog({
  timeZone,
  onClose,
  onSaved,
}: {
  timeZone: string;
  onClose: () => void;
  onSaved: (adjusted: boolean) => void;
}) {
  const { products, locations, loading } = useCatalog();
  const [requestError, setRequestError] = useState<string>();
  const form = useForm<AdjustmentForm>({
    resolver: zodResolver(adjustmentSchema),
    defaultValues: {
      productId: '',
      locationId: '',
      lotId: '',
      countedQuantity: '',
      note: '',
      occurredOn: todayCivilDate(timeZone),
    },
  });
  useSingleLocation(locations, (id) => form.setValue('locationId', id));
  const productId = useWatch({ control: form.control, name: 'productId' });
  const occurredOn = useWatch({ control: form.control, name: 'occurredOn' });
  const locationId = useWatch({ control: form.control, name: 'locationId' });
  const product = products.find((item) => item.id === productId);

  async function save(data: AdjustmentForm) {
    setRequestError(undefined);
    try {
      const movement = await inventoryMovementsApi.adjust({
        productId: data.productId,
        locationId: data.locationId,
        countedQuantity: data.countedQuantity.replace(',', '.'),
        note: data.note,
        occurredOn: data.occurredOn,
        ...(data.lotId ? { lotId: data.lotId } : {}),
      });
      onSaved(movement !== null);
    } catch (cause) {
      setRequestError(getApiError(cause));
    }
  }

  return (
    <Modal
      open
      onOpenChange={onClose}
      title="Ajustar contagem"
      description="Informe quanto encontrou no local. A diferença é calculada aqui e fica registrada junto com a contagem."
    >
      {loading ? (
        <LoadingCard label="Carregando catálogo" />
      ) : (
        <form noValidate onSubmit={(event) => void form.handleSubmit(save)(event)}>
          <div className="grid gap-4 sm:grid-cols-2">
            <ProductField
              products={products}
              value={productId}
              error={form.formState.errors.productId?.message}
              onChange={(value) => form.setValue('productId', value, { shouldValidate: true })}
            />
            {!loading && locations.length === 0 ? (
              <NoStockLocationNotice />
            ) : (
              <Select
                error={form.formState.errors.locationId?.message}
                label="Local"
                value={locationId}
                options={locationOptions(locations, 'Escolha o local')}
                onValueChange={(value) =>
                  form.setValue('locationId', value, { shouldValidate: true })
                }
              />
            )}
            <Input
              label={`Encontrei${product ? ` (${shortUnit(product.baseUnit)})` : ''}`}
              inputMode="decimal"
              error={form.formState.errors.countedQuantity?.message}
              {...form.register('countedQuantity')}
            />
            <DateField
              label="Quando"
              timeZone={timeZone}
              value={occurredOn}
              onChange={(value) => form.setValue('occurredOn', value, { shouldValidate: true })}
            />
            <div className="sm:col-span-2">
              <Textarea
                label="O que explica a diferença"
                rows={2}
                error={form.formState.errors.note?.message}
                {...form.register('note')}
              />
            </div>
          </div>
          {requestError ? <RequestAlert message={requestError} /> : null}
          <Footer label="Ajustar contagem" onClose={onClose} form={form} />
        </form>
      )}
    </Modal>
  );
}

function TransferDialog({
  timeZone,
  onClose,
  onSaved,
}: {
  timeZone: string;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { products, locations, loading } = useCatalog();
  const { showUnits } = useInventoryUnits();
  // O destino pode estar noutra unidade que a pessoa alcança (ADR 0039).
  const destinations = useQuery({
    queryKey: ['company', 'inventory', 'locations', 'ACTIVE', 'REACHABLE'],
    queryFn: () => inventoryApi.listLocations('ACTIVE', 'REACHABLE'),
    retry: false,
  });
  const [requestError, setRequestError] = useState<string>();
  const form = useForm<TransferForm>({
    resolver: zodResolver(transferSchema),
    defaultValues: {
      productId: '',
      originLocationId: '',
      destinationLocationId: '',
      quantity: '',
      lotId: '',
      note: '',
      occurredOn: todayCivilDate(timeZone),
    },
  });
  useSingleLocation(locations, (id) => form.setValue('originLocationId', id));
  const productId = useWatch({ control: form.control, name: 'productId' });
  const occurredOn = useWatch({ control: form.control, name: 'occurredOn' });
  const originLocationId = useWatch({ control: form.control, name: 'originLocationId' });
  const destinationLocationId = useWatch({ control: form.control, name: 'destinationLocationId' });
  const product = products.find((item) => item.id === productId);

  async function save(data: TransferForm) {
    setRequestError(undefined);
    try {
      await inventoryMovementsApi.transfer({
        productId: data.productId,
        originLocationId: data.originLocationId,
        destinationLocationId: data.destinationLocationId,
        quantity: { baseQuantity: data.quantity.replace(',', '.') },
        note: data.note || null,
        occurredOn: data.occurredOn,
        ...(data.lotId ? { lotId: data.lotId } : {}),
      });
      await onSaved();
    } catch (cause) {
      setRequestError(getApiError(cause));
    }
  }

  return (
    <Modal
      open
      onOpenChange={onClose}
      title="Transferir entre locais"
      description="A saída e a entrada nascem juntas, na mesma transação: não existe mercadoria em trânsito entre dois locais da empresa. O destino pode ser de outra unidade sua."
    >
      {loading ? (
        <LoadingCard label="Carregando catálogo" />
      ) : (
        <form noValidate onSubmit={(event) => void form.handleSubmit(save)(event)}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <ProductField
                products={products}
                value={productId}
                error={form.formState.errors.productId?.message}
                onChange={(value) => form.setValue('productId', value, { shouldValidate: true })}
              />
            </div>
            {!loading && locations.length === 0 ? <NoStockLocationNotice /> : null}
            <Select
              error={form.formState.errors.originLocationId?.message}
              label="De"
              value={originLocationId}
              options={locationOptions(locations, 'Escolha a origem')}
              onValueChange={(value) =>
                form.setValue('originLocationId', value, { shouldValidate: true })
              }
            />
            <Select
              error={form.formState.errors.destinationLocationId?.message}
              label="Para"
              value={destinationLocationId}
              options={[
                { value: '', label: 'Escolha o destino' },
                ...(destinations.data ?? locations).map((item) => ({
                  value: item.id,
                  label:
                    showUnits && 'businessUnitName' in item
                      ? `${item.displayName} · ${item.businessUnitName}`
                      : item.displayName,
                })),
              ]}
              onValueChange={(value) =>
                form.setValue('destinationLocationId', value, { shouldValidate: true })
              }
            />
            <Input
              label={`Quantidade${product ? ` (${shortUnit(product.baseUnit)})` : ''}`}
              inputMode="decimal"
              error={form.formState.errors.quantity?.message}
              {...form.register('quantity')}
            />
            <DateField
              label="Quando"
              timeZone={timeZone}
              value={occurredOn}
              onChange={(value) => form.setValue('occurredOn', value, { shouldValidate: true })}
            />
            <div className="sm:col-span-2">
              <Textarea label="Observação" rows={2} {...form.register('note')} />
            </div>
          </div>
          {requestError ? <RequestAlert message={requestError} /> : null}
          <Footer label="Transferir" onClose={onClose} form={form} />
        </form>
      )}
    </Modal>
  );
}

function Footer({
  label,
  onClose,
  form,
}: {
  label: string;
  onClose: () => void;
  form: { formState: { isSubmitting: boolean } };
}) {
  return (
    <div className="mt-6 flex flex-wrap items-center justify-end gap-3 border-t border-surface-border pt-5">
      <FormErrorSummary className="basis-full sm:basis-auto sm:flex-1" />
      <Button type="button" variant="outline" onClick={onClose}>
        Cancelar
      </Button>
      <Button loading={form.formState.isSubmitting}>{label}</Button>
    </div>
  );
}

/// Em que se conta a quantidade: na embalagem escolhida em "Contar em", ou na unidade-base.
function countingUnit(
  product: InventoryProduct | undefined,
  chosen: InventoryProduct['packages'][number] | undefined,
): string {
  if (chosen) return chosen.displayName.toLowerCase();
  return product ? shortUnit(product.baseUnit) : 'unidade-base';
}

/// Contando em embalagem, mostra quanto isso dá na unidade-base: "5 frascos" vira "2.500 ml".
function countingHint(
  product: InventoryProduct | undefined,
  chosen: InventoryProduct['packages'][number] | undefined,
  amount: string,
): string | undefined {
  if (!product || !chosen) {
    return product && product.packages.some((item) => item.active)
      ? 'Chegou em embalagem? Escolha em “Contar em” e informe quantas.'
      : undefined;
  }
  const unit = shortUnit(product.baseUnit);
  const each = `Cada ${chosen.displayName.toLowerCase()} tem ${trimQuantity(chosen.baseQuantity)} ${unit}`;
  const count = Number(amount.replace(',', '.'));
  if (!amount || !Number.isFinite(count) || count <= 0) return `${each}.`;
  const total = (count * Number(chosen.baseQuantity)).toLocaleString('pt-BR', {
    maximumFractionDigits: 3,
  });
  return `${each}: entram ${total} ${unit} no estoque.`;
}

function quantityOf(value: string, packageId: string): InventoryQuantityInput {
  const amount = value.replace(',', '.');
  return packageId ? { packageId, packageQuantity: amount } : { baseQuantity: amount };
}
